import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { FadeTextComponent } from '@shared/components/ellipsis-one-row/fade-text.component';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestTicketInfo } from '@shared/interfaces/turnover.interface';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { AvatarModule } from '@ui-components/components/avatar/avatar.module';
import { IconComponent } from '@ui-components/components/icon/icon.component';
import { InputModule } from '@ui-components/controls/input/input.module';

export interface ScheduleTicketSelectDialogData {
  tickets: RestTicketInfo[];
  userMap: Map<number, IRadioButtonOption<RestUserModel>>;
}

@UntilDestroy()
@Component({
  selector: 'app-schedule-ticket-select-dialog',
  templateUrl: './schedule-ticket-select-dialog.component.html',
  styleUrls: ['./schedule-ticket-select-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    InputModule,
    AvatarModule,
    FadeTextComponent,
    IconComponent,
  ],
})
export class ScheduleTicketSelectDialogComponent extends PuSubscribable implements OnInit {
  public dialogRef: MatDialogRef<ScheduleTicketSelectDialogComponent> = inject(MatDialogRef);
  public data: ScheduleTicketSelectDialogData = inject(MAT_DIALOG_DATA);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  EIcon = EIcon;
  EColorPalette = EColorPalette;

  searchControl = new FormControl(null);
  filteredTickets: RestTicketInfo[] = [];

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.filteredTickets = [...this.data.tickets];

    this.searchControl.valueChanges
      .pipe(startWith(''), debounceTime(200), distinctUntilChanged())
      .subscribe(searchStr => {
        this.filterTickets(searchStr);
      })
      .untilDestroyed(this);
  }

  private filterTickets(search?: string): void {
    const normalized = (search || '').trim().toLowerCase();

    if (!normalized) {
      this.filteredTickets = [...this.data.tickets];
    } else {
      this.filteredTickets = this.data.tickets.filter(ticket => ticket.title?.toLowerCase().includes(normalized));
    }

    this.cdr.markForCheck();
  }

  onTicketSelect(ticket: RestTicketInfo): void {
    this.dialogRef.close(ticket);
  }

  close(): void {
    this.dialogRef.close();
  }
}
